import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, Grid, IconButton, Tab, Tabs, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import { push } from '@lagunovsky/redux-react-router'
import i18n from 'simple-react-i18n'
import IAEauAction from './IAEauAction'
import { nbPerPageLabel } from '../referencial/constants/ReferencialConstants'
import {
    MODEL_OBJECTIVES_LABEL,
    MODEL_STATUS,
    MODEL_TYPES,
    MODEL_TYPES_LABEL,
} from './constants/IAEauConstants'
import { getFullDate } from '../utils/DateUtil'
import { getModelStatusIcon, getModelStatusLabel } from './utils/IAEauUtils'
import ToastrAction from '../toastr/actions/ToastrAction'
import useDebounce from 'utils/customHook/useDebounce'
import useTitle from 'utils/customHook/useTitle'
import { CardTable } from 'components/datatable/NewTable'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import ColorOval from 'components/icon/ColorOval'
import { filterAllCharacters, getI18nOrLabel } from 'utils/StringUtil'
import { setConfirmationModal } from 'utils/FormUtils'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SimpleInput from 'components/forms/SimpleInput'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { CardTitle } from 'components/card/NewCard'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import CityAction from 'referencial/components/city/actions/CityAction'
import useActions from 'utils/customHook/useActions'
import IAEauModelsResultsPanel from './components/results/IAEauModelsResultsPanel'

const TAB_TABLE = 'table'
const TAB_RESULTS = 'results'

const IAEauGlobalModelsApp = ({ stationType }) => {
    const dispatch = useDispatch()

    const MODEL_TYPE_OPTIONS = useMemo(() => [
        { value: MODEL_TYPES.PERCEPTRON, label: i18n.perceptron },
        { value: MODEL_TYPES.AUGURES, label: i18n.omenTrends },
        { value: MODEL_TYPES.ARIMA, label: i18n.arima },
        { value: MODEL_TYPES.TARISSEMENT, label: i18n.dryingUp },
        { value: MODEL_TYPES.METEO_NAPPES, label: i18n.meteoNappes },
        { value: MODEL_TYPES.HYPE, label: i18n.hype },
        { value: MODEL_TYPES.TARRI_BRGM, label: i18n.tarissementBrgm },
        { value: MODEL_TYPES.SCIKIT_LEARN_GENERIC, label: i18n.scikit },
        { value: MODEL_TYPES.CAMELS, label: i18n.camels },
        { value: MODEL_TYPES.CYDRE, label: i18n.cydre },
    ], [])

    const { iaeauModels, citiesIndex } = useSelector(store => ({
        iaeauModels: store.IAEauReducer.iaeauModels.filter(m => m.typeModel !== MODEL_TYPES.HYDRATEC),
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const [filter, setFilter] = useState({ searchValue: '', typeModel: '' })
    const [filterResults, setFilterResults] = useState([])
    const [savedFilter, setSavedFilter] = useState()
    const [activeTab, setActiveTab] = useState(TAB_TABLE)

    useTitle(() => [{
        title: i18n[stationType],
        href: stationType,
    }, {
        title: i18n.models,
        href: `${stationType}/iaeauModels`,
    }], [stationType])

    useActions(() => [], [])

    const { isLoaded, progress } = useProgressDispatch(() => [
        dispatch(IAEauAction.getAllModels(stationType)),
        dispatch(CityAction.fetchCities()),
    ], [dispatch, stationType])

    const launchModel = (model) => {
        if (!model.idStation) return
        IAEauAction.launchModel(stationType, parseInt(model.idStation, 10), model).then(() => {
            dispatch(ToastrAction.success(i18n.jobLaunched))
            dispatch(IAEauAction.getAllModels(stationType))
        })
    }

    const tableData = iaeauModels.map(model => {
        const stationId = parseInt(model.idStation, 10)
        const pushtoModel = () => dispatch(push(`/station/${stationType}/${stationId}/iaeauModels/${model.idModel}`))
        const city = citiesIndex[model.townCode]
        const cityLabel = city
            ? (model.townCode ? `${city.name} [${model.townCode}]` : city.name)
            : (model.townCode ? `[${model.townCode}]` : '')
        return {
            deleteIcon: [MODEL_TYPES.ARIMA, MODEL_TYPES.AUGURES, MODEL_TYPES.SCIKIT_LEARN_GENERIC, MODEL_TYPES.CAMELS].includes(model.typeModel) && (
                <Tooltip title={i18n.delete}>
                    <IconButton
                        sx={{ color: '#000', fontSize: '23px', padding: '1px' }}
                        onClick={() => {
                            if (!stationId) return
                            setConfirmationModal(() => IAEauAction.deleteModel(stationType, stationId, model).then(() => dispatch(IAEauAction.getAllModels(stationType))))
                        }}
                    >
                        <DeleteIcon fontSize='inherit' />
                    </IconButton>
                </Tooltip>
            ),
            playIcon: (
                <Tooltip title={i18n.runCalculation}>
                    <IconButton onClick={() => launchModel(model)} sx={{ color: '#000', fontSize: '23px', padding: '1px' }}>
                        <PlayCircleOutlineIcon fontSize='inherit' />
                    </IconButton>
                </Tooltip>
            ),
            name: { value: model.name, onClick: pushtoModel },
            stationCode: { value: model.stationCode, onClick: pushtoModel },
            stationName: { value: model.stationName, onClick: pushtoModel },
            city: { value: cityLabel, onClick: pushtoModel },
            type: { value: MODEL_TYPE_OPTIONS.find(o => o.value === model.typeModel)?.label || getI18nOrLabel(MODEL_TYPES_LABEL[model.typeModel]), onClick: pushtoModel },
            objective: { value: model.objective && i18n[MODEL_OBJECTIVES_LABEL[model.objective]], onClick: pushtoModel },
            horizon: { value: model.horizon, onClick: pushtoModel },
            confidenceIndicator: { value: model.confidenceIndicator, onClick: pushtoModel },
            creation: { value: getFullDate(model.creationDate), onClick: pushtoModel },
            statusIcon: (
                <Tooltip title={getModelStatusLabel(model.statusCode)}>
                    <IconButton
                        size='actionTable'
                        onClick={() => launchModel(model)}
                    >
                        {getModelStatusIcon(model.statusCode, 25)}
                    </IconButton>
                </Tooltip>
            ),
            lastExecution: { value: getFullDate(model.calculationDate || model.updateDate || model.simulationDate), onClick: pushtoModel },
            dashboard: { value: <SimpleCheckbox checked={model.displayDashboard} checboxStyle={{ height: '25px', width: '25px' }} disabled />, onClick: pushtoModel },
            situation: { value: <SimpleCheckbox checked={model.displaySituation} checboxStyle={{ height: '25px', width: '25px' }} disabled />, onClick: pushtoModel },
            alert: { value: <SimpleCheckbox checked={model.displayAlert} checboxStyle={{ height: '25px', width: '25px' }} disabled />, onClick: pushtoModel },
            color: model.color ? {
                value: <ColorOval color={model.color} />,
                sortValue: model.color,
                onClick: pushtoModel,
            } : { value: '', onClick: pushtoModel },
            idStation: model.idStation,
            idModel: model.idModel,
            typeModel: model.typeModel,
        }
    })

    useDebounce(() => {
        if (iaeauModels.some(m => [MODEL_STATUS.PROGRESS, MODEL_STATUS.DEMAND_PROGRESS, MODEL_STATUS.DEMAND_SENT, MODEL_STATUS.WAITING, MODEL_STATUS.LEARNING_PROGRESS].includes(m.statusCode))) {
            dispatch(IAEauAction.getAllModels(stationType))
        }
    }, 5000, [iaeauModels, dispatch, stationType])

    if (!isLoaded) {
        return <ProgressCard progress={progress} withMessage round />
    }

    const filteredByType = filter.typeModel
        ? tableData.filter(row => row.typeModel === filter.typeModel)
        : tableData
    const filteredByFavorite = filterResults?.length
        ? filteredByType.filter(row => filterResults.some(r => r.idStation === row.idStation))
        : filteredByType
    const filteredData = filterAllCharacters(filteredByFavorite, ['name', 'city', 'stationCode', 'stationName', 'type', 'objective', 'horizon'], filter.searchValue)

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} variant='fullWidth'>
                    <Tab value={TAB_TABLE} label={i18n.table} />
                    <Tab value={TAB_RESULTS} label={i18n.results} />
                </Tabs>
            </Grid>
            {activeTab === TAB_TABLE && (
                <>
                    <Grid size={12}>
                        <Card>
                            <CardTitle title={i18n.filters} color='white' />
                            <CardContent>
                                <Grid container spacing={1}>
                                    <Grid size={4}>
                                        <SimpleInput
                                            title={i18n.search}
                                            value={filter.searchValue}
                                            onChange={v => setFilter(prev => ({ ...prev, searchValue: v }))}
                                            onEnterKeyPress={v => setFilter(prev => ({ ...prev, searchValue: v }))}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <SuperMultiAutocomplete
                                            label={i18n.modelType}
                                            options={MODEL_TYPE_OPTIONS}
                                            keyLabel='label'
                                            keyValue='value'
                                            values={filter.typeModel}
                                            onChange={v => setFilter(prev => ({ ...prev, typeModel: v ?? '' }))}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <SimpleFilterSelect
                                            stationType={stationType}
                                            stations={iaeauModels.map(m => ({ ...m, id: m.idStation, code: m.stationCode }))}
                                            onChange={(results, newFilter) => {
                                                setFilterResults(results)
                                                setSavedFilter(newFilter)
                                            }}
                                            value={savedFilter}
                                            launchSearch={() => {}}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={12}>
                        <CardTable
                            title={i18n.models}
                            rows={filteredData}
                            rowsPerPageOptions={nbPerPageLabel}
                            headers={[
                                { key: 'deleteIcon', value: '' },
                                { key: 'playIcon', value: '' },
                                'name',
                                'city',
                                'stationName',
                                'type',
                                'objective',
                                'horizon',
                                'confidenceIndicator',
                                'creation',
                                { key: 'statusIcon', value: '' },
                                'lastExecution',
                                'dashboard',
                                'situation',
                                'alert',
                                'color',
                            ]}
                        />
                    </Grid>
                </>
            )}
            {activeTab === TAB_RESULTS && (
                <Grid size={12}>
                    <IAEauModelsResultsPanel stationType={stationType} />
                </Grid>
            )}
        </Grid>
    )
}

IAEauGlobalModelsApp.propTypes = {
    stationType: PropTypes.string.isRequired,
}

export default IAEauGlobalModelsApp
